import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';
import CourtAppearanceForm from './CourtAppearanceForm';

function CourtHistory({ juvenileId }) {
  const [offenses, setOffenses] = useState([]);
  const [appearances, setAppearances] = useState([]);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAppearance, setEditingAppearance] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchAllData = useCallback(async () => {
    if (!juvenileId) return;
    try {
      setError('');
      const [offensesRes, appearancesRes] = await Promise.all([
        apiClient.get(`/api/juveniles/${juvenileId}/offenses`),
        apiClient.get(`/api/case/juveniles/${juvenileId}/court`)
      ]);

      // **THE FIX IS HERE:** We extract the 'offenses' array from the response object.
      setOffenses(offensesRes.data.offenses || []);
      setAppearances(appearancesRes.data);
    } catch (err) {
      setError('Failed to fetch court history.');
      console.error(err);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleFormSubmit = async (formData) => {
    try {
      if (editingAppearance) {
        await apiClient.put(`/api/case/court/${editingAppearance.id}`, formData);
      } else {
        await apiClient.post(`/api/case/offenses/${formData.offense_id}/court`, formData);
      }
      setEditingAppearance(null);
      setShowAddForm(false);
      fetchAllData();
    } catch (err) {
      console.error("Failed to save court appearance", err);
      alert(err.response?.data?.msg || 'Failed to save court appearance.');
    }
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 mb-0">Court History</h2>
        {hasPermission('add-court-appearance') && !showAddForm && !editingAppearance && (
          <button onClick={() => setShowAddForm(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            Add Appearance
          </button>
        )}
      </div>

      {showAddForm && (
        <CourtAppearanceForm
          offenses={offenses}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingAppearance && (
        <CourtAppearanceForm
          offenses={offenses}
          onSubmit={handleFormSubmit}
          initialData={editingAppearance}
          onCancel={() => setEditingAppearance(null)}
        />
      )}

      <div className="mt-4 space-y-4">
        {appearances.length > 0 ? (
          appearances.map(app => (
            <div key={app.id} className="p-4 border rounded-lg bg-slate-50">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-bold text-slate-700">Offense: {app.offense_type} (Case: {app.case_number})</h4>
                  <p className="text-sm text-slate-500">Appearance Date: {formatDate(app.court_date)}</p>
                </div>
                {hasPermission('add-court-appearance') && (
                <button onClick={() => setEditingAppearance(app)} className="text-sm text-slate-600 hover:text-gold">Edit</button>
                )}
              </div>
              <div className="border-t mt-2 pt-2 text-sm space-y-1">
                <p><strong>Judge:</strong> {app.judge_name || 'N/A'}</p>
                <p><strong>Plea:</strong> {app.plea || 'N/A'}</p>
                <p><strong>Verdict:</strong> {app.verdict || 'N/A'}</p>
                <p><strong>Sentence:</strong> {app.sentence_details || 'N/A'}</p>
              </div>
            </div>
          ))
        ) : (
          !showAddForm && !editingAppearance && <p>No court appearances recorded.</p>
        )}
      </div>
    </div>
  );
}

export default CourtHistory;